import { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import ReactTooltip from 'react-tooltip';
import styles from '../../Graph.module.scss';
import { getColorKeyFromIndex, getHeatMapData } from '../../../../../../store/dashboard/transformer';
// import { graphDataType } from '../../../../../../store/Dashboard/types';

interface MapDataType {
    id: string;
    state: string;
    value: number;
}
const INDIA_TOPO_JSON = require('./india.topo.json');

const DEFAULT_COLOR = '#ffffff';

const getRandomInt = () => {
    let value = Math.random() * 100;
    return Number(value.toFixed(3));
};

const geographyStyle = {
    default: {
        outline: 'none',
    },
    hover: {
        // fill: '#2a52be',
        // fill: '#324ab2',
        fill: '#54579d',
        transition: 'all 250ms',
        outline: 'none',
    },
    pressed: {
        // fill: '#54579d',
        fill: '#324ab2',
        outline: 'none',
    },
};

// will generate random heatmap data on every call

export interface GstCoverageStateData {
    stateName: string;
    count: number;
    activeCount: number;
    inactiveCount: number;
}

// IndiaCustomMap is used in two modes that need different data shapes:
//   - `variant: 'default'`      → existing heatmap callers pass graphData + graphName
//   - `variant: 'gst-coverage'` → new GST map (StatesMap) passes stateData + onStateHover
//
// We use a discriminated union (rather than a single interface with everything
// optional) so TypeScript catches misuse at compile time:
//   - the 'default' variant rejects stateData via `stateData?: never`
//   - the 'gst-coverage' variant *requires* stateData (no optional fallback)
//   - existing heatmap callers don't have to start passing GST props
//
// If we ever drop the heatmap variant, this can collapse to a single
// IndiaCustomMapGstProps. Until then, the union is doing real work.
interface IndiaCustomMapBaseProps {
    height: number;
    width: number;
    error: boolean;
}

interface IndiaCustomMapDefaultProps extends IndiaCustomMapBaseProps {
    variant?: 'default';
    graphData: any;
    graphName: string;
    stateData?: never;
}

interface IndiaCustomMapGstProps extends IndiaCustomMapBaseProps {
    variant: 'gst-coverage';
    stateData: Record<string, GstCoverageStateData>;
    onStateHover?: (stateName: string | null, anchorEl: SVGElement | null) => void;
    graphData?: any;
    graphName?: string;
}

type IndiaCustomMapProps = IndiaCustomMapDefaultProps | IndiaCustomMapGstProps;

const GST_HAS_DATA_COLOR = '#F2E8FF';
const GST_NO_DATA_COLOR = '#FFFFFF';
const GST_BOUNDARY_COLOR = '#6021B3';

// india.topo.json uses non-standard / typo'd names for a few states that don't match
// what Karza / GSTN return (and what our BE forwards in stateData). Normalize here so
// the map colours and hover popover both line up. Ladakh isn't in this topology at all
// (file predates the 2019 reorg) — Ladakh GSTINs will remain uncoloured on the map
// until we swap the topology file in a separate ticket. RECUR-26035.
const TOPOLOGY_TO_CANONICAL_STATE_NAME: Record<string, string> = {
    'NCT of Delhi': 'Delhi',
    'Andaman & Nicobar Island': 'Andaman & Nicobar Islands',
    'Arunanchal Pradesh': 'Arunachal Pradesh',
    'Dadara & Nagar Havelli': 'Dadra & Nagar Haveli',
};

const canonicalStateName = (topologyName: string): string => TOPOLOGY_TO_CANONICAL_STATE_NAME[topologyName] ?? topologyName;

function IndiaCustomMap(props: IndiaCustomMapProps) {
    const isGstVariant = props.variant === 'gst-coverage';
    // GST variant feeds real SVG dimensions; derive scale from those so the
    // map fills its allotted box. Legacy callers still get the window-based scale.
    const scale = isGstVariant ? props.width * 1.75 : (window.innerWidth * 140) / 1920;
    const PROJECTION_CONFIG = {
        scale: scale,
        center: [82, 23], // always in [East Latitude, North Longitude]
    };

    // Default (heatmap) mode
    const graphData = props.graphData;
    const colors = !isGstVariant ? graphData?.colors : undefined;
    const metadata = !isGstVariant ? graphData?.metadata : undefined;
    const plotValues = !isGstVariant ? graphData?.plots?.[0]?.plotValues || [] : [];
    const [tooltipContent, setTooltipContent] = useState('');
    const [totalCalculated, setTotalCalculated] = useState(0);
    const heatMapRanges = !isGstVariant ? metadata?.heatMapRanges || [] : [];
    const [data, setData] = useState<MapDataType[]>(!isGstVariant ? getHeatMapData(plotValues, colors, heatMapRanges) : []);

    //light to dark color
    //@ts-ignore
    // const colorScales = scaleLinear().domain([0, 100]).range([colors?.primary, colors?.secondary]);

    useEffect(() => {
        if (!isGstVariant && plotValues?.length) {
            setTotalCalculated(
                plotValues?.reduce((acc, curr) => {
                    acc += !Number.isNaN(curr.value) ? curr.value : 0;
                    return acc;
                }, 0),
            );
        }
    }, [plotValues]);

    useEffect(() => {
        if (!isGstVariant) {
            setData(getHeatMapData(plotValues, colors, heatMapRanges));
        }
    }, [plotValues, colors, heatMapRanges]);

    const getGstFillColor = (geoName: string): string => {
        if (!isGstVariant || !props.stateData) return DEFAULT_COLOR;
        const upperName = canonicalStateName(geoName)?.toUpperCase();
        const entry = Object.values(props.stateData).find((s) => s.stateName?.toUpperCase() === upperName);
        if (!entry || entry.count === 0) return GST_NO_DATA_COLOR;
        return GST_HAS_DATA_COLOR;
    };

    // No GST data for a state ⇒ skip the hover affordance entirely (no fill
    // change, no cursor, no tooltip fire). Otherwise the user sees an
    // interactive hover that opens an empty popover. Per UAT.
    const hasGstDataForGeo = (geoName: string): boolean => {
        if (!isGstVariant || !props.stateData) return false;
        const upperName = canonicalStateName(geoName)?.toUpperCase();
        const entry = Object.values(props.stateData).find((s) => s.stateName?.toUpperCase() === upperName);
        return !!entry && entry.count > 0;
    };

    const onMouseEnterGst = (geo) => {
        return (e: React.MouseEvent<SVGElement>) => {
            if (!isGstVariant) return;
            // Don't fire hover for no-data states — parent won't render the popover.
            if (!hasGstDataForGeo(geo.properties.name)) return;
            if (props.onStateHover) {
                // Pass the canonical (BE-aligned) state name so StatesMap doesn't have
                // to know about topology naming quirks.
                props.onStateHover(canonicalStateName(geo.properties.name), e.currentTarget as SVGElement);
            }
        };
    };

    const onMouseLeaveGst = () => {
        if (isGstVariant && props.onStateHover) {
            props.onStateHover(null, null);
        }
    };

    const onMouseEnter = (geo, current: MapDataType | undefined) => {
        const percentageCalc = current?.value && totalCalculated ? ((current?.value / totalCalculated) * 100).toFixed(2) : 0;
        return () => {
            setTooltipContent(
                `${geo.properties.name}: ${(current?.value || 0)?.toLocaleString('en-IN')} ${
                    percentageCalc ? '(' + percentageCalc + '%)' : ''
                }`,
            );
        };
    };

    const onMouseLeave = () => {
        setTooltipContent('');
    };

    const gstGeographyStyle = {
        default: {
            outline: 'none',
            stroke: GST_BOUNDARY_COLOR,
            strokeWidth: 0.5,
        },
        hover: {
            fill: GST_BOUNDARY_COLOR,
            stroke: GST_BOUNDARY_COLOR,
            strokeWidth: 0.5,
            transition: 'all 200ms',
            outline: 'none',
            cursor: 'pointer',
        },
        pressed: {
            fill: GST_BOUNDARY_COLOR,
            stroke: GST_BOUNDARY_COLOR,
            strokeWidth: 0.5,
            outline: 'none',
        },
    };

    // No-data states: hover keeps the default fill / cursor so the state looks
    // inert. Avoids the "interactive but empty" UX flagged in UAT.
    const gstGeographyStyleNoData = {
        default: gstGeographyStyle.default,
        hover: gstGeographyStyle.default,
        pressed: gstGeographyStyle.default,
    };

    return (
        <div
            className={styles.indiawrapper}
            style={
                isGstVariant
                    ? { width: '100%', height: '100%', overflow: 'visible', display: 'block' }
                    : { height: `${props.height * 2 + 190}px` }
            }
        >
            <ReactTooltip>{tooltipContent}</ReactTooltip>
            <ComposableMap
                /* @ts-ignore */

                projectionConfig={PROJECTION_CONFIG}
                projection="geoMercator"
                width={isGstVariant ? props.width : props.width / 9.5}
                height={props.height}
                data-tip=""
                style={isGstVariant ? { width: '100%', height: '100%', overflow: 'visible' } : undefined}
            >
                <Geographies geography={INDIA_TOPO_JSON}>
                    {({ geographies }) =>
                        geographies.map((geo) => {
                            if (isGstVariant) {
                                const hasData = hasGstDataForGeo(geo.properties.name);
                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={getGstFillColor(geo.properties.name) as unknown as string}
                                        style={hasData ? gstGeographyStyle : gstGeographyStyleNoData}
                                        onMouseEnter={hasData ? onMouseEnterGst(geo) : undefined}
                                        // onMouseLeave stays wired on every state — it just clears the
                                        // hovered state in the parent. Without this, moving the cursor
                                        // from a data state directly onto a no-data state would leave
                                        // the popover stuck open.
                                        onMouseLeave={onMouseLeaveGst}
                                    />
                                );
                            }
                            const current = data.find((s) => s.id === geo.id);
                            return (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    /* @ts-ignore */
                                    fill={current?.color || DEFAULT_COLOR}
                                    style={geographyStyle}
                                    onMouseEnter={onMouseEnter(geo, current)}
                                    onMouseLeave={onMouseLeave}
                                />
                            );
                        })
                    }
                </Geographies>
            </ComposableMap>
            {!isGstVariant && (
                <div className={styles.mapDataWrapper}>
                    {heatMapRanges?.map((range, i) => (
                        <div className={styles.datawrapper} key={`${range?.range}-${i}`}>
                            <div className={styles.roundedDiv} style={{ background: colors?.[getColorKeyFromIndex(i)] }}></div>
                            <p className={styles.mapBoldDatawrapper}>{range?.range}</p>

                            <p className={styles.mapLigitTextColor}>({range?.text})</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default IndiaCustomMap;
